import { isMobile } from "react-device-detect";
import useWindowSize from "../../hooks/useWindowSize";
import { BREAKPOINT } from "./../_helpers";
import { useLoaderTheme } from "./useLoaderTheme";
import { getRouteArtistSlug } from "@/utils/routePaths";

export interface ArtistPageLoaderProps {
  artistName?: string | null;
}

const routeArtistName = () => {
  if (typeof window === "undefined") return "Truevined";

  const parts = window.location.pathname.split("/").filter(Boolean);
  const routeName = getRouteArtistSlug(parts, "Truevined");

  return formatArtistName(routeName);
};

const formatArtistName = (name?: string | null) => {
  if (!name) return "Truevined";

  return name
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const ArtistPageLoader = ({ artistName }: ArtistPageLoaderProps) => {
  const windowSize = useWindowSize();
  const reallyIsMobile = isMobile || windowSize.width <= BREAKPOINT;
  const theme = useLoaderTheme({ neutral: true });
  const displayName = formatArtistName(artistName || routeArtistName());
  const pageWidth = reallyIsMobile ? "calc(100vw - 5rem)" : `calc(${windowSize.width}px - (2.5rem * 2))`;

  const line = (width: string, height = reallyIsMobile ? "20px" : "14px") => (
    <span
      style={{
        display: "block",
        width,
        height,
        borderRadius: "0.5rem",
        backgroundColor: theme.skeletonBase,
        animation: "artistLoaderPulse 1.9s ease-in-out infinite",
      }}
    />
  );

  return (
    <div
      className="artist-page-loader no-custom-cursor"
      style={{
        width: "100vw",
        minHeight: "100vh",
        backgroundColor: theme.background,
        color: theme.text,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <style>
        {`
          @keyframes artistLoaderPulse {
            0%, 100% { opacity: 0.42; }
            50% { opacity: 0.88; }
          }
          @keyframes artistLoaderRing {
            0%, 100% { transform: scale(1); opacity: 0.42; }
            50% { transform: scale(1.035); opacity: 0.75; }
          }
          @keyframes artistLoaderSweep {
            0% { transform: translateX(-115%); }
            100% { transform: translateX(115%); }
          }
          .artist-page-loader * { box-sizing: border-box; }
          .artist-loader-sweep { position: relative; overflow: hidden; }
          .artist-loader-sweep::after {
            content: "";
            position: absolute;
            inset: 0;
            background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.11) 50%, transparent 100%);
            animation: artistLoaderSweep 1.85s ease-in-out infinite;
            pointer-events: none;
          }
        `}
      </style>

      <div
        style={{
          position: "fixed",
          inset: 0,
          width: "100%",
          minHeight: "100vh",
          backgroundColor: theme.background,
          pointerEvents: "none",
        }}
      />

      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          minHeight: reallyIsMobile ? "86px" : "92px",
          padding: reallyIsMobile ? "1.5rem 2.5rem" : "1.6rem 3rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          zIndex: 100,
          backgroundColor: theme.skeletonBase,
          backdropFilter: "blur(12px)",
        }}
      >
        <div
          style={{
            fontFamily: "Bellefair, serif",
            fontSize: reallyIsMobile ? "2.4rem" : "2.1rem",
            lineHeight: 1,
            color: theme.text,
            textTransform: "uppercase",
          }}
        >
          {displayName}
        </div>
        <div
          style={{
            width: reallyIsMobile ? "5rem" : "2.8125rem",
            height: reallyIsMobile ? "5rem" : "2.8125rem",
            position: "relative",
          }}
        >
          <span
            style={{
              position: "absolute",
              top: reallyIsMobile ? "1.9rem" : "1.0625rem",
              left: 0,
              width: "100%",
              height: reallyIsMobile ? "0.35rem" : "0.125rem",
              borderRadius: "999px",
              background: theme.text,
            }}
          />
          <span
            style={{
              position: "absolute",
              top: reallyIsMobile ? "2.85rem" : "1.5rem",
              right: 0,
              width: "70%",
              height: reallyIsMobile ? "0.35rem" : "0.125rem",
              borderRadius: "999px",
              background: theme.text,
            }}
          />
        </div>
      </nav>

      <main
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <section
          className="HeroSection"
          style={{
            marginTop: reallyIsMobile ? "105px" : "9.1rem",
            position: "relative",
            display: "inline-flex",
            width: "100%",
            minHeight: reallyIsMobile ? "calc(100vh - 105px)" : "calc(100vh - 9.1rem)",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "3.0625rem",
          }}
        >
          <div
            style={{
              width: reallyIsMobile ? "28rem" : "12.25rem",
              height: reallyIsMobile ? "28rem" : "12.25rem",
              position: "relative",
              marginTop: "2rem",
              marginBottom: reallyIsMobile ? "7rem" : "2rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                position: "absolute",
                width: reallyIsMobile ? "41rem" : "17rem",
                height: reallyIsMobile ? "41rem" : "17rem",
                borderRadius: "999px",
                border: `${reallyIsMobile ? "0.65rem" : "0.3125rem"} solid ${theme.skeletonHighlight}`,
                opacity: 0.36,
                animation: "artistLoaderRing 2.4s ease-in-out infinite",
              }}
            />
            <div
              className="artist-loader-sweep"
              style={{
                width: reallyIsMobile ? "26.3rem" : "11rem",
                height: reallyIsMobile ? "26.3rem" : "11rem",
                borderRadius: "28rem",
                background: `radial-gradient(circle at 38% 30%, ${theme.skeletonHighlight}, ${theme.skeletonBase})`,
                border: `1px solid ${theme.skeletonHighlight}`,
              }}
            />
            <div
              style={{
                width: reallyIsMobile ? "29rem" : "12.25rem",
                height: reallyIsMobile ? "29rem" : "12.25rem",
                position: "absolute",
                borderRadius: "28rem",
                border: `1px solid ${theme.skeletonHighlight}`,
                opacity: 0.28,
              }}
            />
          </div>

          <h1
            style={{
              margin: 0,
              color: theme.text,
              fontFamily: "Bellefair, serif",
              fontSize: reallyIsMobile ? "5.6rem" : windowSize.width >= 1200 ? "8.75rem" : "7rem",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "normal",
              textTransform: "uppercase",
              textAlign: "center",
              maxWidth: "calc(100vw - 2rem)",
              overflowWrap: "break-word",
            }}
          >
            {displayName}
          </h1>

          <div
            style={{
              width: "77%",
              maxWidth: "60rem",
              borderRadius: "1rem",
              display: "flex",
              flexDirection: "column",
              gap: "1.16rem",
              alignItems: "center",
            }}
          >
            {line("100%")}
            {line("100%")}
            {line("86%")}
          </div>

          <div
            style={{
              color: theme.accent,
              fontSize: reallyIsMobile ? "2.75rem" : "1.75rem",
              fontFamily: "Bellefair, serif",
              fontWeight: 400,
              textTransform: "uppercase",
              marginTop: reallyIsMobile ? "17rem" : "7rem",
              opacity: 0.86,
            }}
          >
            SCROLL
          </div>
        </section>

        <section
          className="music-section"
          style={{
            width: pageWidth,
            maxWidth: reallyIsMobile ? "calc(100vw - 5rem)" : "none",
            padding: reallyIsMobile ? "2.5rem" : "3.5rem",
            margin: reallyIsMobile ? "1rem 2.5rem 0" : "12.25rem 2.5rem 0",
            backgroundColor: theme.skeletonBase,
            borderRadius: "1.875rem",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: reallyIsMobile ? "10.5rem" : "5rem",
          }}
        >
          {!reallyIsMobile && (
            <div
              style={{
                color: theme.accent,
                fontSize: "60px",
                fontFamily: "Hanson, Poppins, sans-serif",
                fontWeight: 700,
                margin: "1.75rem 0 0.5rem",
              }}
            >
              MUSIC
            </div>
          )}

          <div
            style={{
              width: "100%",
              minHeight: reallyIsMobile ? "auto" : "30.625rem",
              display: "flex",
              flexDirection: reallyIsMobile ? "column" : "row",
              gap: reallyIsMobile ? "1rem" : "5rem",
              position: "relative",
            }}
          >
            <div
              style={{
                width: reallyIsMobile ? "100%" : "35%",
                display: "flex",
                flexDirection: "column",
                alignItems: reallyIsMobile ? "center" : "flex-start",
                gap: reallyIsMobile ? "1.5rem" : "2.1875rem",
                paddingTop: "0.8125rem",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "1.5rem", width: "100%" }}>
                <div
                  style={{
                    color: theme.accent,
                    fontSize: reallyIsMobile ? "2.5rem" : "1.5rem",
                    fontFamily: "Hanson, Poppins, sans-serif",
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  Newest release
                </div>
                <div
                  style={{
                    minWidth: reallyIsMobile ? "6.5rem" : "5.6875rem",
                    height: reallyIsMobile ? "2.25rem" : "1.125rem",
                    borderTopRightRadius: "0.875rem",
                    borderBottomRightRadius: "0.875rem",
                    backgroundColor: theme.accent,
                    opacity: 0.52,
                  }}
                />
              </div>

              <div
                className="artist-loader-sweep"
                style={{
                  minWidth: reallyIsMobile ? "100%" : "17.0625rem",
                  width: reallyIsMobile ? "100%" : "17.0625rem",
                  height: reallyIsMobile ? "auto" : "17.0625rem",
                  minHeight: reallyIsMobile ? "auto" : "17.0625rem",
                  aspectRatio: "1 / 1",
                  background: `linear-gradient(135deg, ${theme.skeletonHighlight}, ${theme.skeletonBase})`,
                  borderRadius: "10px",
                }}
              />

              {reallyIsMobile && (
                <div style={{ width: "100%", display: "grid", gap: "1rem" }}>
                  {line("76%", "18px")}
                  {line("58%", "16px")}
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      gap: "0.7rem",
                      marginTop: "0.4rem",
                    }}
                  >
                    {["Music", "Albums", "Videos"].map((label) => (
                      <div
                        key={label}
                        style={{
                          flex: 1,
                          minHeight: "64px",
                          borderRadius: "8px",
                          backgroundColor: theme.skeletonHighlight,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: theme.background,
                          fontFamily: "Poppins",
                          fontWeight: 700,
                          fontSize: "15px",
                          opacity: 0.78,
                        }}
                      >
                        {label}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {!reallyIsMobile && (
              <div
                style={{
                  width: "65%",
                  minHeight: "30.625rem",
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  gap: "1.25rem",
                  alignContent: "start",
                }}
              >
                {Array.from({ length: 8 }).map((_, index) => (
                  <div key={index} style={{ display: "grid", gap: "0.75rem" }}>
                    <div
                      className="artist-loader-sweep"
                      style={{
                        aspectRatio: "1 / 1",
                        borderRadius: "10px",
                        background: `linear-gradient(135deg, ${theme.skeletonHighlight}, ${theme.skeletonBase})`,
                      }}
                    />
                    {line(index % 2 === 0 ? "78%" : "62%", "11px")}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section
          className="BioSection"
          style={{
            width: pageWidth,
            padding: reallyIsMobile ? "4rem 2rem" : "3.5rem",
            margin: reallyIsMobile ? "1rem 2.5rem 0" : "12.25rem 2.5rem 0",
            backgroundColor: theme.skeletonBase,
            borderRadius: "1.875rem",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "2.5rem",
          }}
        >
          <div
            style={{
              color: theme.accent,
              fontSize: reallyIsMobile ? "3rem" : "3.34rem",
              fontFamily: "Hanson, Poppins, sans-serif",
              fontWeight: 700,
            }}
          >
            BIO
          </div>
          <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {line("100%", "1.25rem")}
            {line("90%", "1.25rem")}
            {line("75%", "1.25rem")}
          </div>
        </section>
      </main>
    </div>
  );
};

export default ArtistPageLoader;
